import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as MetaMaskIcon } from '@pkg/assets/icons/v2/metamask.svg';
import { ButtonDeprecated } from '@pkg/components';
import { useAppSelector, useIsMounted, useMetaMask, useMobile } from '@pkg/hooks';

import { apiFactory } from '@app/client/apiFactory/api';
import { AssetTypes } from '@app/client/common/structs/general';
import config from '@app/config';

import './MetaMaskConnectionButton.scss';

interface MetaMaskConnectButtonProps {
    successCallback?: () => void;
    buttonCopy?: string;
    size?: 'hero' | 'large' | 'small';
    assetType?: AssetTypes;
}

export const MetaMaskConnectButton = ({
    successCallback,
    buttonCopy,
    size = 'large',
    assetType = AssetTypes.Yat,
}: MetaMaskConnectButtonProps) => {
    const { t } = useTranslation(['nft']);
    const [buttonText, setButtonText] = useState<string>(buttonCopy ? buttonCopy : t('sign_in_to_metamask'));
    const { connect, isMetaMaskConnected, isMetaMaskInstalled, onboarding } = useMetaMask();

    const { currentEmoji } = useAppSelector((state) => state.userEmojis);
    const { selectedArtifact } = useAppSelector((state) => state.artifacts);
    const { isMobile } = useMobile();
    const isMounted = useIsMounted();

    useEffect(() => {
        let buttonLabel = buttonCopy ? buttonCopy : t('sign_in_to_metamask');
        if (isMetaMaskConnected) {
            buttonLabel = buttonCopy ? buttonCopy : t('continue_with_metamask');
            onboarding.current?.stopOnboarding();
        }

        if (isMounted()) {
            setButtonText(buttonLabel);
        }
    }, [isMetaMaskConnected, isMounted, t, onboarding, buttonCopy]);

    const redirectUrl = useMemo(() => {
        if (assetType === AssetTypes.Artifact) {
            return `&redirect=/mint-artifact/${selectedArtifact?.artifact_type}/${selectedArtifact?.artifactId}`;
        } else {
            return `&redirect=/mint/${currentEmoji}`;
        }
    }, [assetType, selectedArtifact, currentEmoji]);

    const mobileNotInstalledCallback = useCallback(() => {
        const publicUrl = config.publicUrl;
        const refreshToken = apiFactory().token()?.refresh_token || '';
        if (refreshToken) {
            const magicLink = `${publicUrl}/?refresh_token=${refreshToken}${redirectUrl || ''}`;
            window.open(`https://metamask.app.link/dapp/${magicLink}`);
        }
    }, [redirectUrl]);

    const onClick = () => {
        if (isMetaMaskInstalled && successCallback) {
            successCallback();
            return;
        }
        connect();
        return;
    };

    return (
        <div className="yat-metamask-button">
            <div className="yat-metamask-button__desktop">
                <ButtonDeprecated
                    classes={['yat-metamask-connection-button']}
                    color="primary"
                    icon={<MetaMaskIcon />}
                    onClick={!isMobile && !isMetaMaskInstalled ? mobileNotInstalledCallback : onClick}
                    size={size}
                >
                    {buttonText}
                </ButtonDeprecated>
            </div>
            <div className="yat-metamask-button__mobile">
                <ButtonDeprecated
                    classes={['yat-metamask-connection-button']}
                    color="primary"
                    fluid
                    icon={<MetaMaskIcon />}
                    onClick={isMobile && !isMetaMaskInstalled ? mobileNotInstalledCallback : onClick}
                    size="large"
                >
                    {buttonText}
                </ButtonDeprecated>
            </div>
        </div>
    );
};
